import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface RequirementCard {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  accepted: boolean | null;
}

const AdidasBeforeStartView: React.FC = () => {
  const navigate = useNavigate();
  const [requirements, setRequirements] = useState<RequirementCard[]>([
    {
      id: 'brand',
      title: 'Adidas or Y-3 Brand Only',
      description: 'We accept authentic Adidas and Y-3 footwear. Other brands are not eligible.',
      icon: (
        <svg className="w-12 h-12" viewBox="0 0 69 32" fill="currentColor">
          <path d="M68.56 4.05c-.4-.15-5.86-.95-9.84 2.01-3.98 2.96-17.14 17.37-17.14 17.37s-12.87-8.36-13.93-8.97c-1.06-.61-6.85-3.29-11.11 1.21-4.26 4.5-12.15 12.67-12.15 12.67s-.55.54-.08.8c.47.26 1.09-.01 1.09-.01s20.22-8.5 22.01-9.32c1.79-.82 6.13-2.51 10.18 1.06 4.05 3.57 15.89 13.32 15.89 13.32s3.21 2.72 6.23.86c3.02-1.86 9.1-6.04 9.1-6.04s.87-.69.17-1.26c-.7-.57-1.43.11-1.43.11s-7.66 4.4-9.52 5.32c-1.86.92-4.21 1.1-7.04-1.47-2.83-2.57-13.97-12.2-13.97-12.2s10.66-11.77 13.46-14.33c2.8-2.56 7.69-4.4 12.89-.47 0 0 3.54 2.69 4.25 3.17.71.48 1.62.59 1.62-.28 0-.87-.68-1.55-.68-1.55z"/>
        </svg>
      ),
      accepted: null,
    },
    {
      id: 'condition',
      title: 'Clean & Wearable',
      description: 'Shoes should be clean with no major sole damage, tears, or permanent stains.',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      accepted: null,
    },
    {
      id: 'type',
      title: 'Footwear Focus',
      description: 'We primarily accept shoes. Cleats with metal spikes are not eligible.',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
      accepted: null,
    },
  ]);

  const handleAccept = (id: string, accepted: boolean) => {
    setRequirements(prev =>
      prev.map(req => (req.id === id ? { ...req, accepted } : req))
    );
  };

  const allAccepted = requirements.every(req => req.accepted === true);
  const anyDeclined = requirements.some(req => req.accepted === false);

  // Auto-navigate when all accepted
  useEffect(() => {
    if (allAccepted) {
      const timer = setTimeout(() => {
        navigate('/adidas/scan');
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [allAccepted, navigate]);

  const handleBack = () => {
    navigate('/adidas/takeback');
  };

  return (
    <div className="min-h-screen bg-[#111111] flex flex-col">
      {/* Header */}
      <header className="py-6 px-8 flex items-center justify-between">
        <button
          onClick={handleBack}
          className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="flex items-center gap-3">
          <span className="text-[#000000] font-bold uppercase tracking-widest text-sm">Adidas Certified Pre-Owned</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-8 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h1 className="text-4xl font-bold text-white mb-3">
            Before We Start
          </h1>
          <p className="text-lg text-white/60">
            Confirm your items meet these requirements
          </p>
        </motion.div>

        {/* Requirement Cards */}
        <div className="grid grid-cols-3 gap-6 max-w-5xl w-full">
          {requirements.map((req, index) => (
            <motion.div
              key={req.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className={`
                relative rounded-2xl p-6 text-center border-2 transition-all duration-300
                ${req.accepted === true
                  ? 'bg-[#000000]/10 border-[#000000]'
                  : req.accepted === false
                    ? 'bg-red-500/10 border-red-500'
                    : 'bg-white/5 border-white/20 hover:border-white/40'}
              `}
            >
              {/* Checkmark overlay */}
              <AnimatePresence>
                {req.accepted === true && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="absolute top-4 right-4 w-8 h-8 bg-[#000000] rounded-full flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className={`mb-4 flex justify-center ${req.accepted === true ? 'text-[#000000]' : 'text-white/60'}`}>
                {req.icon}
              </div>
              <h3 className="text-white font-bold text-lg mb-2">{req.title}</h3>
              <p className="text-white/60 text-sm mb-6">{req.description}</p>

              {/* Accept/Decline buttons */}
              {req.accepted === null && (
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => handleAccept(req.id, true)}
                    className="px-6 py-2 bg-[#000000] text-white rounded-full font-medium hover:bg-[#E04E00] transition-colors"
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => handleAccept(req.id, false)}
                    className="px-6 py-2 bg-white/10 text-white rounded-full font-medium hover:bg-white/20 transition-colors"
                  >
                    No
                  </button>
                </div>
              )}

              {req.accepted === false && (
                <p className="text-red-400 text-sm">
                  This item may not be eligible
                </p>
              )}
            </motion.div>
          ))}
        </div>

        {/* Status message */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 text-center"
        >
          {allAccepted && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 text-[#000000] font-medium"
            >
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Proceeding to scan...
            </motion.div>
          )}
          {anyDeclined && !allAccepted && (
            <p className="text-white/60">
              Some items may not be eligible. You can still proceed with eligible items.
            </p>
          )}
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-4 px-8 text-center text-white/40 text-sm border-t border-white/10">
        Powered by Pentatonic AI
      </footer>
    </div>
  );
};

export default AdidasBeforeStartView;
